// The filters of a collection's list, as the address bar holds them (phase 3
// §3.4) — the same names the API reads, so a link, the list and its counts
// agree:
//   f.<key>=<value>        field values, repeated: OR within a key
//   min.<key>= max.<key>=  ranges (numbers; dates YYYY[-MM[-DD]]);
//                          min.@year / max.@year for the year
//   place=<id>|none        places (and everything under them)
//   done=1|0 rated=1|0     my "seen", my rating
//   avg=1..5               the members' average, at least

export const YEAR = '@year';
export const NO_PLACE = 'none';
const FLAGS = ['done', 'rated', 'avg'];

export const emptyFilters = () => ({ values: {}, ranges: {}, places: [], done: null, rated: null, avg: null });

/** The filters of a URLSearchParams (other parameters ignored). */
export const filtersFromParams = (params) => {
  const filters = emptyFilters();
  params.forEach((raw, name) => {
    const value = String(raw).trim();
    let match;
    if ((match = name.match(/^f\.([a-z][a-z0-9_]{0,63})$/))) {
      if (value === '') return;
      const key = match[1];
      const list = filters.values[key] || (filters.values[key] = []);
      if (!list.includes(value)) list.push(value);
    } else if ((match = name.match(/^(min|max)\.(@year|[a-z][a-z0-9_]{0,63})$/))) {
      if (value === '') return;
      const range = filters.ranges[match[2]] || (filters.ranges[match[2]] = { min: null, max: null });
      range[match[1]] = value;
    } else if (name === 'place') {
      if ((value === NO_PLACE || /^[1-9][0-9]*$/.test(value)) && !filters.places.includes(value)) filters.places.push(value);
    } else if (name === 'done' || name === 'rated') {
      if (value === '1' || value === '0') filters[name] = value;
    } else if (name === 'avg') {
      if (/^[1-5]$/.test(value)) filters.avg = value;
    }
  });
  return filters;
};

/** Appends the filters to `params`, always in the same order (keys as given, then ranges, places, mine). */
export const writeFilters = (params, filters) => {
  if (!filters) return params;
  Object.entries(filters.values || {}).forEach(([key, values]) => values.forEach((value) => params.append(`f.${key}`, value)));
  Object.entries(filters.ranges || {}).forEach(([key, range]) => {
    if (range.min !== null && range.min !== undefined && range.min !== '') params.append(`min.${key}`, range.min);
    if (range.max !== null && range.max !== undefined && range.max !== '') params.append(`max.${key}`, range.max);
  });
  (filters.places || []).forEach((place) => params.append('place', place));
  FLAGS.forEach((name) => { if (filters[name] !== null && filters[name] !== undefined) params.append(name, filters[name]); });
  return params;
};

/** The filters as axios params (repeated names as arrays: serialize with indexes: null). */
export const filterApiParams = (filters) => {
  const out = {};
  writeFilters(new URLSearchParams(), filters).forEach((value, name) => {
    if (name in out) out[name] = [].concat(out[name], value);
    else out[name] = (name.startsWith('f.') || name === 'place') ? [value] : value;
  });
  return out;
};

/**
 * Every parameter of a query string as axios params, repeated names kept
 * as arrays (send with paramsSerializer: SERIALIZE): what the list sends.
 */
export const queryParams = (params) => {
  const out = {};
  params.forEach((value, name) => {
    if (name in out) out[name] = [].concat(out[name], value);
    else out[name] = value;
  });
  return out;
};

/** axios: repeated names as name=a&name=b, not name[]=a. */
export const SERIALIZE = { indexes: null };

/** How many things are filtered on: a value, a range, a place, a flag each. */
export const filterCount = (filters) => (filters
  ? Object.values(filters.values).reduce((n, values) => n + values.length, 0)
    + Object.keys(filters.ranges).length + filters.places.length
    + FLAGS.filter((name) => filters[name] !== null).length
  : 0);

export const hasFilters = (filters) => filterCount(filters) > 0;

const clone = (filters) => ({
  ...filters,
  values: Object.fromEntries(Object.entries(filters.values).map(([k, v]) => [k, [...v]])),
  ranges: Object.fromEntries(Object.entries(filters.ranges).map(([k, v]) => [k, { ...v }])),
  places: [...filters.places],
});

/** Ticks or unticks a value of a field (OR within the field). */
export const toggleValue = (filters, key, value) => {
  const next = clone(filters);
  const list = next.values[key] || [];
  next.values[key] = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  if (next.values[key].length === 0) delete next.values[key];
  return next;
};

/** Sets a range ({min, max}, either empty); both empty removes it. */
export const setRange = (filters, key, range) => {
  const next = clone(filters);
  const min = range.min === undefined || range.min === null ? '' : String(range.min).trim();
  const max = range.max === undefined || range.max === null ? '' : String(range.max).trim();
  if (min === '' && max === '') delete next.ranges[key];
  else next.ranges[key] = { min: min === '' ? null : min, max: max === '' ? null : max };
  return next;
};

export const togglePlace = (filters, place) => {
  const next = clone(filters);
  next.places = next.places.includes(place) ? next.places.filter((p) => p !== place) : [...next.places, place];
  return next;
};

/** done, rated or avg: a value, or null to drop it. */
export const setFlag = (filters, name, value) => ({ ...clone(filters), [name]: value === null || value === undefined ? null : String(value) });

/** One active filter removed: {kind: 'value'|'range'|'place'|'flag', key, value}. */
export const removeFilter = (filters, chip) => {
  switch (chip.kind) {
    case 'value': return toggleValue(filters, chip.key, chip.value);
    case 'range': return setRange(filters, chip.key, {});
    case 'place': return togglePlace(filters, chip.value);
    default: return setFlag(filters, chip.key, null);
  }
};

// --- Decades (the Year facet writes min.@year / max.@year) ---

/** The decades a year range covers whole, among those listed. */
export const decadesIn = (range, decades) => {
  if (!range) return [];
  const min = range.min !== null && range.min !== undefined ? parseInt(range.min, 10) : -Infinity;
  const max = range.max !== null && range.max !== undefined ? parseInt(range.max, 10) : Infinity;
  return decades.filter((decade) => decade >= min && decade + 9 <= max);
};

/**
 * The range after ticking or unticking a decade. Ticked decades make one
 * span (a range is one min and one max): ticking widens it to take the
 * decade in; unticking the first decade starts it after, unticking any
 * other ends it before.
 */
export const toggleDecade = (range, decade, decades) => {
  const ticked = decadesIn(range, decades).sort((a, b) => a - b);
  if (!ticked.includes(decade)) {
    const all = [...ticked, decade];
    return { min: String(Math.min(...all)), max: String(Math.max(...all) + 9) };
  }
  const rest = decade === ticked[0] ? ticked.filter((d) => d > decade) : ticked.filter((d) => d < decade);
  if (rest.length === 0) return { min: null, max: null };
  return { min: String(rest[0]), max: String(rest[rest.length - 1] + 9) };
};

/**
 * A date bound as typed — YYYY, YYYY-MM or YYYY-MM-DD, single-digit month or
 * day accepted — written YYYY[-MM[-DD]]; '' for none, null when it is not one.
 */
export const normalizeDate = (text) => {
  const value = String(text ?? '').trim();
  if (value === '') return '';
  const m = value.match(/^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?$/);
  if (!m) return null;
  const pad = (n) => String(n).padStart(2, '0');
  if (m[2] === undefined) return m[1];
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  if (m[3] === undefined) return `${m[1]}-${pad(month)}`;
  const day = Number(m[3]);
  const last = new Date(Date.UTC(Number(m[1]), month, 0)).getUTCDate();
  if (day < 1 || day > last) return null;
  return `${m[1]}-${pad(month)}-${pad(day)}`;
};
